<template>
  <div class="fts-login" :data-login-load="loading || !loaded ? 'loading' : 'loaded'">
    <!-- Two-panel layout -->
    <div class="auth-panel">
      <!-- Left panel -->
      <div class="auth-panel-left">
        <div class="auth-panel-left-inner">
          <!-- Progress indicator: Details · Verify · Workspace, lit by the sign-up step -->
          <div class="auth-progress" :class="{ 'auth-progress--labelled': state == 'createAccount' }"
            v-if="(state == 'login' || state == 'createAccount') && !isJoinExistingWorkspace">
            <span class="auth-progress-step" v-for="(label, i) in signupSteps" :key="label">
              <span class="auth-progress-pip" :class="{ active: i + 1 === signupStep, done: i + 1 < signupStep }"></span>
              <span class="auth-progress-label" v-if="state == 'createAccount'">{{ label }}</span>
            </span>
          </div>

          <!-- Auth error banner (auto-logout) -->
          <div class="login-auth-error" v-if="!!logoutError">
            <div>
              You have been auto-logged out. {{ logoutError }}. Try login again.
            </div>
          </div>

          <!-- Form content -->
          <div class="login-form comfort-form-design" v-if="loaded && !isError">

            <!-- join -->
            <div :key="state" class="login-form-state" v-if="state == 'join'">
              <div class="login-form-title">
                <div class="login-form-title-main">Join a workspace</div>
                <div class="login-form-title-sub">You are joining an existing workspace</div>
              </div>

              <div class="login-form-join-workspace-wrapper">
                <div class="login-form-join-workspace-icon">
                  {{ getWorkspaceShortName(formData.workspace) }}
                </div>
                <div class="login-form-join-workspace">
                  {{ formData.workspace }}
                </div>
              </div>

              <!-- MFA setup (SS05898): 2FA-enforced workspace — scan the QR then enter the code to join -->
              <div class="login-form-body" v-if="qrCode">
                <div class="login-form-fields">
                  <div style="text-align: center;">
                    <qrcode-vue :value="qrCode" :size="160" render-as="svg" foreground="var(--fts-text)"
                      background="var(--fts-card-background)" />
                    <span style="display: block; margin-top: 6px; font-size: small;">Please use Authenticator App scan QR Code above</span>
                  </div>
                  <fts-login-form-element v-if="otp" type="code" v-model="formData.otp" />
                </div>
              </div>

              <div class="login-form-actions">
                <fts-btn class="login-form-action" color="primary" text-transform-init box-shadow-none
                  @click="existingUserJoinWorkspace">
                  <template v-if="qrCode">Verify &amp; join</template>
                  <template v-else-if="formData.existing">Join now</template>
                  <template v-else>Activate</template>
                </fts-btn>

                <router-link :to="{ name: 'dashboard' }" custom v-slot="{ navigate }">
                  <fts-btn text class="login-form-action" text-transform-init box-shadow-none @click.native="navigate">
                    may be later
                  </fts-btn>
                </router-link>
              </div>
            </div>

            <!-- activate -->
            <div :key="state" class="login-form-state" v-else-if="state == 'activate'">
              <div class="login-form-title">
                <div class="login-form-title-main">Activation</div>
                <div class="login-form-title-sub">Please wait a moment.</div>
              </div>
            </div>

            <!-- verify: the "Verify and continue" link from the code email, or the legacy verification link -->
            <div :key="state" class="login-form-state" v-else-if="state == 'verify' || state == 'verify-email'">
              <div class="login-form-title">
                <div class="login-form-title-main">{{ verifyLanding.title }}</div>
                <div class="login-form-title-sub">{{ verifyLanding.description }}</div>
              </div>
              <div class="login-form-actions" v-if="verifyLanding.done">
                <fts-btn class="login-form-action" color="primary" text-transform-init box-shadow-none
                  @click="$router.push({ name: 'login' })">
                  {{ verifyLanding.action || 'Back to sign in' }}
                </fts-btn>
              </div>
            </div>

            <!-- forgot password -->
            <div :key="state" class="login-form-state" v-else-if="state == 'forgotPassword'">
              <div class="login-form-title">
                <div class="login-form-title-main">{{ metaInfo.title || 'Reset password' }}</div>
                <div class="login-form-title-sub">
                  Don't worry, happens to the best of us.
                  Just fill in your email and we'll send you a link to reset your password.
                </div>
              </div>

              <form @submit.prevent="requestReset" autocomplete="off" novalidate class="login-form-body">
                <div class="login-form-fields">
                  <fts-login-form-element type="username" v-model="formData.username"
                    :validator="$v.formData && $v.formData.username" display-name="Email" />
                </div>
                <div class="login-form-actions">
                  <fts-btn type="submit" class="login-form-action" color="primary" text-transform-init box-shadow-none>
                    Get reset link
                  </fts-btn>
                  <div class="divider">
                    <div class="divider-label">OR</div>
                  </div>
                  <router-link :to="{ name: 'login' }" custom v-slot="{ navigate }">
                    <fts-btn text class="login-form-action" color="primary" text-transform-init box-shadow-none
                      @click.native="navigate">
                      Back to sign in
                    </fts-btn>
                  </router-link>
                </div>
              </form>
            </div>

            <!-- reset -->
            <div :key="state" class="login-form-state" v-else-if="state == 'reset'">
              <div class="login-form-title">
                <div class="login-form-title-main">{{ metaInfo.title || 'Set new password' }}</div>
                <div class="login-form-title-sub" v-if="formData.username">for {{ formData.username }}.</div>
                <div class="login-form-title-sub" v-else>One more step to recover your account !</div>
              </div>

              <form @submit.prevent="resetPassword" autocomplete="off" novalidate class="login-form-body">
                <div class="login-form-fields">
                  <fts-login-form-element type="password" v-model="formData.password"
                    :validator="$v.formData && $v.formData.password" display-name="Password" password-hint />

                  <fts-login-form-element type="password" v-model="formData.confirmPassword"
                    :validator="$v.formData && $v.formData.confirmPassword" display-name="Confirm password"
                    placeholder="Confirm password" />
                </div>
                <div class="login-form-actions">
                  <fts-btn type="submit" class="login-form-action" color="primary" text-transform-init box-shadow-none>
                    Reset
                  </fts-btn>
                </div>
              </form>
            </div>

            <!-- create account -->
            <div :key="state" class="login-form-state" v-else-if="state == 'createAccount'">
              <div class="login-form-title">
                <div class="login-form-title-main">{{ verification && !otp ? 'Check your email' : (metaInfo.title || 'Sign up') }}</div>
                <div class="login-form-title-sub" v-if="verification && !otp">
                  We sent a 6-digit code to <span class="fts-font-medium">{{ verification.email }}</span>.
                  Enter it here to finish creating your account.
                </div>
                <div class="login-form-title-sub" v-else-if="isJoinExistingWorkspace">You are joining an existing workspace</div>
                <div class="login-form-title-sub" v-else-if="joinExistingWorkspace && invitationCodeStep == 2">
                  You are about to join the workspace
                </div>
                <div class="login-form-title-sub" v-else>Let's get started. Just a few short steps!</div>
              </div>

              <!-- join existing workspace -->
              <template v-if="isJoinExistingWorkspace">
                <div class="login-form-join-workspace-wrapper">
                  <div class="login-form-join-workspace-icon">
                    {{ getWorkspaceShortName(formData.workspace) }}
                  </div>
                  <div class="login-form-join-workspace">
                    {{ formData.workspace }}
                  </div>
                </div>
              </template>

              <!-- want to join existing workspace -->
              <div class="login-form-join-existing" v-else>
                <fts-checkbox v-model="joinExistingWorkspace" v-if="invitationCodeStep != 2">
                  <div class="login-form-join-existing-label">I wish to join an existing workspace.</div>
                </fts-checkbox>

                <template v-if="joinExistingWorkspace">
                  <template v-if="invitationCodeStep == 2">
                    <div class="login-form-join-workspace-wrapper">
                      <div class="login-form-join-workspace-icon">
                        {{ getWorkspaceShortName(invitationCodeFormData.workspace) }}
                      </div>
                      <div class="login-form-join-workspace">
                        {{ invitationCodeFormData.workspace }}
                      </div>
                    </div>

                    <div class="login-form-action-message">
                      Kindly confirm that this is the correct workspace. If it is not, you may click <span
                        class="fts-font-medium">Cancel</span> to enter a different code.
                    </div>

                    <div class="login-form-actions">
                      <fts-btn class="login-form-action" color="primary" text-transform-init box-shadow-none
                        @click="setInvitationCodeStep(3)">
                        Join workspace
                      </fts-btn>
                      <fts-btn class="login-form-action" color="default" text text-transform-init box-shadow-none
                        @click="setInvitationCodeStep(1)">
                        Cancel
                      </fts-btn>
                    </div>
                  </template>
                  <!-- to-do remove (pending API) -->
                  <template v-else-if="$route.query.dev == 1">
                    <div class="login-form-join-existing-helper">
                      You need to have an invitation code to join an existing workspace.
                      You may ask the workspace admin or user admin to send you an invitation code.
                    </div>

                    <form @submit.prevent="setInvitationCodeStep(2)" autocomplete="off" novalidate class="login-form-body">
                      <div class="login-form-fields">
                        <fts-login-form-element type="code" v-model="invitationCodeFormData.invitationCode"
                          :validator="$v.invitationCodeFormData && $v.invitationCodeFormData.invitationCode"
                          display-name="Invitation code" placeholder="Invitation code" />
                      </div>
                      <div class="login-form-actions">
                        <fts-btn type="submit" class="login-form-action" color="primary" text-transform-init box-shadow-none
                          :loading="invitationCodeLoading">
                          Next
                        </fts-btn>
                        <fts-btn class="login-form-action" color="default" text text-transform-init box-shadow-none
                          @click="joinExistingWorkspace = false">
                          Cancel
                        </fts-btn>
                      </div>
                    </form>
                  </template>
                  <!-- to-do remove (pending API) -->
                  <div class="login-form-join-existing-helper" v-else>
                    You need to have an invitation link to join an existing workspace.
                    You may ask the workspace admin or user admin to send you an invitation link.
                  </div>
                </template>
              </div>

              <!-- step 2: the code (docs/identity-email-verification-proposal.md, D-3). The MFA
                   QR/OTP block below takes over once the account exists and the join enforces 2FA. -->
              <template v-if="verification && !otp">
                <fts-verification-code
                  ref="verificationCode"
                  :verification="verification"
                  :busy="verifying"
                  :verified="verificationDone"
                  :error="verificationError"
                  allow-change-email
                  link-hint
                  @submit="verifyCode"
                  @resend="resendCode"
                  @change-email="changeEmail"
                />
              </template>

              <template v-else-if="!joinExistingWorkspace">
                <form @submit.prevent="createAccount" autocomplete="off" novalidate class="login-form-body">
                  <div class="login-form-fields">
                    <fts-login-form-element type="username" v-model="formData.email"
                      :validator="$v.formData && $v.formData.email"
                      :disabled="fixedUsername && $v.formData && $v.formData.email && $v.formData.email.$invalid === false"
                      display-name="Email" />
                    <template v-if="formData.email && !isCreateAccountWithTemplate">
                      <fts-login-form-element type="password" v-model="formData.password"
                        :validator="$v.formData && $v.formData.password" display-name="Password" password-hint />

                      <fts-login-form-element type="password" v-model="formData.confirmPassword"
                        :validator="$v.formData && $v.formData.confirmPassword" display-name="Confirm password"
                        placeholder="Confirm password" />

                      <fts-login-form-element type="display-name" v-model="formData.displayName"
                        :validator="$v.formData && $v.formData.displayName" display-name="Display name" />
                    </template>

                    <!-- MFA setup (SS05894): workspace enforces 2FA — scan the QR then enter the code -->
                    <div v-if="qrCode" style="text-align: center;">
                      <qrcode-vue :value="qrCode" :size="160" render-as="svg" foreground="var(--fts-text)"
                        background="var(--fts-card-background)" />
                      <span style="display: block; margin-top: 6px; font-size: small;">Please use Authenticator App scan QR Code above</span>
                    </div>

                    <fts-login-form-element v-if="otp" type="code" v-model="formData.otp" />
                  </div>
                  <div class="login-form-actions">
                    <fts-btn type="submit" class="login-form-action" color="primary" text-transform-init box-shadow-none>
                      {{ otp ? 'Verify' : 'Sign up' }}
                    </fts-btn>
                    <template v-if="thirdPartyLogin && !formData.email">
                      <div class="divider">
                        <div class="divider-label">or</div>
                      </div>
                      <fts-btn class="login-form-action google-sign-in" color="#ffffff" text-transform-init box-shadow-none
                        @click="googleRegister">
                        <img style="margin-right: 16px;" width="20" src="../../assets/images/brand/google.png" />
                        Continue with Google
                      </fts-btn>
                      <vue-apple-login v-if="isAppleServiceAvailable" color="black" :border="true" type="continue" width="212" height="40"
                        :onSuccess="appleRegisterSuccess" :onFailure="onFailure" class="cust-apple-login" />
                    </template>
                  </div>
                </form>

                <div class="login-form-action-redirect">
                  Already have an account?
                  <router-link :to="{ name: 'login', params: this.$route.params, query: this.$route.query }"
                    class="fts-link-primary">
                    Sign in
                  </router-link>
                </div>

                <div class="login-form-term-policy">
                  By signing up, you agree to Inistate's
                  <a href="https://inistate.com/terms" target="_blank" class="fts-link-primary">terms of services </a>
                  and
                  <a href="https://inistate.com/privacyPolicy" target="_blank" class="fts-link-primary">privacy policy</a>.
                </div>
              </template>
            </div>

            <!-- sign in (default) -->
            <div :key="state" class="login-form-state" v-else>
              <div class="login-form-title">
                <div class="login-form-title-main">
                  <template v-if="isMcpFlow && isUserLoggedIn">Authorize access</template>
                  <template v-else>{{ metaInfo.title || 'Sign in' }}</template>
                </div>
                <div class="login-form-title-sub" v-if="isJoinExistingWorkspace">You are joining an existing workspace</div>
                <div class="login-form-title-sub" v-else-if="isMcpFlow && isUserLoggedIn">
                  Allow <b>{{ mcpClientName }}</b> to access your {{ this.companyName }} account.
                </div>
                <div class="login-form-title-sub" v-else-if="isMcpFlow">
                  Sign in to authorize <b>{{ mcpClientName }}</b> to access your {{ this.companyName }} account.
                </div>
                <div class="login-form-title-sub" v-else>To continue, sign in to {{ this.companyName }}.</div>
              </div>

              <!-- mcp authorizing overlay -->
              <div class="login-mcp-authorizing" v-if="mcpAuthorizing">
                Authorizing <b>{{ mcpClientName }}</b>&hellip;
              </div>

              <!-- mcp consent (already signed in) -->
              <template v-else-if="isMcpFlow && isUserLoggedIn">
                <div class="login-mcp-consent">
                  <div class="login-mcp-consent-account" v-if="getUserDisplayName">
                    Signed in as <b>{{ getUserDisplayName }}</b>
                  </div>
                  <div class="login-mcp-consent-message">
                    <b>{{ mcpClientName }}</b> will access {{ this.companyName }} on your behalf
                    using the same permissions and rights as your account. It can only see and
                    act on the workspaces, modules, and entries that your account is allowed
                    to access.
                  </div>
                </div>
                <div class="login-form-actions">
                  <fts-btn class="login-form-action" color="primary" text-transform-init box-shadow-none
                    @click="authorize">
                    Authorize
                  </fts-btn>
                  <fts-btn class="login-form-action" color="default" text text-transform-init box-shadow-none
                    @click="useDifferentAccount">
                    Use a different account
                  </fts-btn>
                </div>
              </template>

              <!-- regular sign in -->
              <template v-else>
                <!-- join existing workspace -->
                <div class="login-form-join-workspace-wrapper" v-if="isJoinExistingWorkspace">
                  <div class="login-form-join-workspace-icon">
                    {{ getWorkspaceShortName(formData.workspace) }}
                  </div>
                  <div class="login-form-join-workspace">
                    {{ formData.workspace }}
                  </div>
                </div>

                <form @submit.prevent="submit" autocomplete="off" novalidate class="login-form-body">
                  <div class="login-form-fields">
                    <fts-login-form-element type="username" v-model="formData.username" />

                    <fts-login-form-element type="password" v-model="formData.password" />

                    <div v-if="qrCode" style="text-align: center;">
                      <qrcode-vue :value="qrCode" :size="160" render-as="svg" foreground="var(--fts-text)"
                        background="var(--fts-card-background)" />
                      <span style="display: block; margin-top: 6px; font-size: small;">Please use Authenticator App scan QR Code above</span>
                    </div>

                    <fts-login-form-element v-if="otp" type="code" v-model="formData.otp" />

                    <div class="login-form-action-text">
                      <router-link :to="{ name: 'forgotPassword' }" custom v-slot="{ navigate }">
                        <span class="fts-link-primary action-text-btn" @click="navigate">
                          Forgot password?
                        </span>
                      </router-link>
                    </div>
                  </div>

                  <div class="login-form-actions">
                    <fts-btn type="submit" class="login-form-action" color="primary" text-transform-init box-shadow-none>
                      Sign in
                    </fts-btn>
                    <template v-if="thirdPartyLogin">
                      <div class="divider">
                        <div class="divider-label">OR</div>
                      </div>
                      <fts-btn class="login-form-action google-sign-in" color="#ffffff" text-transform-init box-shadow-none
                        @click="googleLogin">
                        <img style="margin-right: 16px;" width="20" src="../../assets/images/brand/google.png" />
                        Continue with Google
                      </fts-btn>
                      <vue-apple-login v-if="isAppleServiceAvailable" color="black" :border="true" type="continue" width="212" height="40"
                        :onSuccess="onSuccess" :onFailure="onFailure" class="cust-apple-login" />
                    </template>
                  </div>
                </form>

                <div class="login-form-action-redirect">
                  Don't have an account?
                  <router-link :to="{ name: 'createAccount', params: this.$route.params, query: this.$route.query }"
                    class="fts-link-primary">
                    Sign up
                  </router-link>
                </div>
              </template>
            </div>
          </div>

          <!-- join error state -->
          <div class="login-error-container" :key="`${state}-error`" v-if="isError && pageState == 'join'">
            <div class="login-error-wrapper">
              <div class="login-error-emphasis-text">Oops!</div>
              <div class="login-error-title">{{ errorMessage.title }}</div>
              <div class="login-error-description">{{ errorMessage.description }}</div>
            </div>
          </div>
          <!-- version footer -->
          <div class="login-footer">
            <div class="app-build-version" v-if="buildVersion">
              version {{ buildVersion }}
            </div>
          </div>

          <!-- feedback indicate -->
          <fts-feedback-indicate ref="ftsFeedbackIndicate" class="login-feedback-indicate">
            <template #action="{ uid, close }">
              <fts-btn color="primary" text text-transform-init @click="close" v-if="uid === 'joinError'">
                Back to sign in
              </fts-btn>
            </template>
          </fts-feedback-indicate>
        </div>
      </div>

      <!-- Right panel -->
      <div class="auth-panel-right">
        <fts-login-illustration />
      </div>
    </div>

    <!-- spinner -->
    <fts-spinner full-screen tabindex="0" ref="loadingOverlay" v-if="loading" />
    <!-- feedback indicate modal -->
    <fts-feedback-indicate modal ref="ftsFeedbackIndicateModal">
      <!-- These MUST stay a single v-if/v-else-if chain, never sibling v-ifs. Vue 2.6 falls back to
           a slot's default content only when the scoped slot yields exactly ONE comment vnode
           (normalizeScopedSlot: `res.length === 1 && vnode.isComment`). Two sibling v-ifs that are
           both false yield TWO comments, which counts as real content — so FTSFeedbackIndicate's
           default Continue / Try Again button silently disappears on every modal whose uid matches
           neither branch (e.g. "Check your mail" after a password reset). One chain = one comment. -->
      <template #action="{ uid, close }">
        <fts-btn color="primary" text text-transform-init @click="close" v-if="uid === 'resetError'">
          Back to sign in
        </fts-btn>
        <fts-btn color="primary" text text-transform-init @click="close(); sendLoginVerificationEmail();"
          v-else-if="uid === 'emailNotVerified'">
          Send verification email
        </fts-btn>
      </template>
    </fts-feedback-indicate>
    <!-- login-user-profile-modal -->
    <fts-login-user-profile-modal ref="ftsLoginUserProfileModal" />
  </div>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required, email, sameAs } from "vuelidate/lib/validators";

import { authorizationStore } from "../store/authorizationStore";
import { clientStore } from '../store/clientStore';
import LocalStorageService from '../store/LocalStorageService';
import { accessTokenExpiring } from '../commons/accessTokenExpiry';
import designValidator from "../commons/designValidator";
import { passwordValidator } from "../commons/validator";
import commons from '../commons/commons';
import webAppConfig from "../commons/webApp.config";
import googleOAuth from "../commons/googleOAuth";
import { DeviceName } from '../commons/enum';
import { takePortalReturn } from '../portals/portalReturn';

import FTSSpinner from "../elements/FTSSpinner";
import FTSBtn from "../elements/FTSBtn";
import FTSFeedbackIndicate from "../elements/FTSFeedbackIndicate";
import FTSCheckbox from '../elements/FTSCheckbox';
import FTSLoginFormElement from './FTSLoginFormElement';
import FTSLoginUserProfileModal from './FTSLoginUserProfileModal';
import FTSLoginIllustration from './FTSLoginIllustration';
import FTSVerificationCode from './FTSVerificationCode';

import VueAppleLogin from "vue-apple-login";

import QrcodeVue from 'qrcode.vue';


Vue.use(VueAppleLogin, {
  clientId: "com.gneysoftware.fetias.service",
  scope: "name email",
  redirectURI: `${window.location.origin}/api/user/AppleSignIn`,
  state: "123",
  usePopup: true,
});

export default {
  name: "login",
  metaInfo() {
    return {
      title: `${this.metaInfo.title} - Inistate`,
    };
  },
  components: {
    'qrcode-vue': QrcodeVue,
    "fts-spinner": FTSSpinner,
    "fts-btn": FTSBtn,
    "fts-feedback-indicate": FTSFeedbackIndicate,
    "fts-checkbox": FTSCheckbox,
    "fts-login-form-element": FTSLoginFormElement,
    "fts-login-user-profile-modal": FTSLoginUserProfileModal,
    "fts-login-illustration": FTSLoginIllustration,
    "fts-verification-code": FTSVerificationCode,
  },
  data: () => ({
    workspaceNameSpecialCharacters: Object.freeze(designValidator.workspaceNameSpecialCharacters.split("")),
    loading: false,
    formData: {},
    invitationCodeLoading: false,
    invitationCodeStep: 1,
    invitationCodeFormData: {},
    loaded: false,
    isError: false,
    errorMessage: {
      title: null,
      description: null
    },
    qrCode: null,
    secret: null,
    otp: false,
    fixedUsername: false,
    joinExistingWorkspace: false,
    buildVersion: webAppConfig.buildVersion,
    isGoogleServiceAvailable: false,
    isAppleServiceAvailable: false,
    mcpAuthorizing: false,
    isUserLoggedIn: false,
    // email verification (docs/identity-email-verification-proposal.md): the session the server
    // returned for this sign-up, and the secret only this tab holds.
    verification: null,
    clientSecret: null,
    verifying: false,
    verificationError: null,
    verificationDone: false,
    verifyLanding: { title: 'Verifying your email', description: 'Please wait a moment.', done: false, action: null },
  }),
  validations() {
    let formData = {};
    let invitationCodeFormData = {};

    if (this.state === "join") {
      formData = {
        displayName: { required },
        username: { required, email },
      };

      if (!this.formData.existing) {
        formData.workspace = {
          required,
          format: designValidator.workspaceNameFormat,
        };
      }

      if (!this.formData.provider) {
        (formData.password = {
          required,
          password: passwordValidator,
        }),
          (formData.confirmPassword = {
            required,
            sameAs: sameAs(() => this.formData.password),
            password: passwordValidator,
          });
      }
    } else if (this.state === "forgotPassword") {
      formData.username = { required, email };
    } else if (this.state === "reset") {
      (formData.password = {
        required,
        password: passwordValidator,
      }),
        (formData.confirmPassword = {
          required,
          sameAs: sameAs(() => this.formData.password),
          password: passwordValidator,
        });
    } else if (this.state === "createAccount") {
      formData.email = { required, email };
      if (!this.isCreateAccountWithTemplate) {
        formData.displayName = { required };
        formData.password = {
          required,
          password: passwordValidator
        };
        formData.confirmPassword = {
          required,
          sameAs: sameAs(() => this.formData.password),
          password: passwordValidator
        };
      }

      if (this.invitationCodeStep == 1) {
        invitationCodeFormData.invitationCode = { required };
      }
    } else {
      formData.username = { required };
      formData.password = { required };
    }

    return { formData, invitationCodeFormData };
  },
  methods: {
    onFocusLoadingOverlay() {
      this.$nextTick(() => {
        this.$refs.loadingOverlay && this.$refs.loadingOverlay.$el && this.$refs.loadingOverlay.$el.focus();
      });
    },
    /* init */
    async init() {
      commons.hideCrispChat();
      // logout
      if (this.state === "logout") {
        authorizationStore.dispatch("logout");
        return;
      }
      /* reset auth */
      if (
        this.state == "reset" ||
        this.state == "activate" ||
        this.state == "verify" ||
        this.state == "verify-email"
      ) {
        LocalStorageService.clearToken();
      }

      if (this.state == "reset") {
        this.formData.username = this.$route.query.email;
        this.formData.code = this.$route.query.code;
        if (!(this.formData.username && this.formData.code)) {
          this.isError = true;
          this.$refs.ftsFeedbackIndicate.show({
            type: "error",
            uid: "joinError",
            desc: "Invalid URL link.",
            handler: () => {
              this.$router.push({ name: 'login' });
              this.isError = false;
            },
          });
        }
      } else if (this.pageState == "join") {
        try {
          this.loading = true;
          const res = await authorizationStore.dispatch("joinWorkspaceInformation", {
            registration: this.$route.query.id,
            code: this.$route.query.code,
          });

          this.formData = res.data;
          if (this.formData.username) {
            this.fixedUsername = true;
            this.formData.email = this.formData.username;
            this.formData.username = null;
          }

          if (LocalStorageService.getAccessToken()) {
            // direct join
          } else if (this.$route.name !== 'createAccount' && this.$route.name !== 'login') {
            this.$router.push({ name: 'createAccount', params: { action: 'join' }, query: this.$route.query })
          }
        } catch (err) {
          this.errorMessage = {
            title: "Something went wrong",
            description: "Please contact the person who give you the link if you have any inquiry."
          }
          if (
            err.response &&
            err.response.status === 400 &&
            err.response.data &&
            err.response.data.message &&
            err.response.data.message.includes("expired")
          ) {
            this.errorMessage.title = err.response.data.message;
          }
          this.isError = true;
        } finally {
          this.loading = false;
        }
      } else if (this.state == "activate") {
        let route = this.$route;
        this.formData = {
          registration: route.query.id,
          code: route.query.code,
        };
        this.activateWorkspace();
      } else if (this.state == "verify" || this.state == "verify-email") {
        await this.handleVerifyLanding();
      } else if (this.state == "createAccount") {
        this.joinExistingWorkspace = false;
        this.resetInvitationCodeForm();
        this.$route.query.ref && $cookies.set('onboarding_ref', this.$route.query.ref, '1y');
      }
      /* redirect */
      this.isUserLoggedIn = Boolean(LocalStorageService.getAccessToken());
      // SS06445: the MCP consent screen shows only for a sign-in that is still good (renewed if need be)
      if (this.isUserLoggedIn && this.isMcpFlow) this.isUserLoggedIn = await this.ensureFreshMcpSignIn();
      if (
        this.pageState !== 'join' &&
        !(this.$route.meta && this.$route.meta.requiresAuth) &&
        this.isUserLoggedIn &&
        // MCP flow: require explicit user consent before POSTing the JWT to the
        // third-party callback. Stay on this page so the consent UI can render.
        !this.isMcpFlow
      ) {
        this.redirect();
      }
    },
    /* redirect */
    async redirect() {
      // MCP OAuth flow: if login was triggered by an MCP client (Claude, GPT, etc.),
      // POST the JWT back to the MCP server instead of navigating to dashboard.
      // mcpCallbackUrl is null when the callback is missing, malformed, or not on
      // the trusted-host allowlist — in those cases we fall through to normal
      // navigation so a crafted link can never exfiltrate the user's JWT.
      const mcpNonce = this.$route.query.mcp_nonce;
      const mcpCallback = this.mcpCallbackUrl;
      if (mcpNonce && mcpCallback) {
        this.mcpAuthorizing = true;
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = mcpCallback.href;
        const fields = {
          nonce: mcpNonce,
          jwt: LocalStorageService.getAccessToken(),
          refreshToken: LocalStorageService.getRefreshToken() || '',
        };
        for (const [k, v] of Object.entries(fields)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = v || '';
          form.appendChild(input);
        }
        document.body.appendChild(form);
        this.$nextTick(() => form.submit());
        return;
      }
      if (this.hasMcpParams && !mcpCallback) {
        // mcp_callback was present but failed validation — surface it so legitimate
        // developers don't get silent failures while staging hosts are missing
        // from webAppConfig.allowedMcpHosts.
        this.$toasted.error('MCP callback URL is not on the trusted allowlist — login aborted.');
        return;
      }

      // Admin-panel SSO handoff: login was initiated by the separate admin SPA. Mint a
      // one-time code on the backend and bounce the browser back to its callback. Only the
      // short-lived code travels in the URL — never the JWT. ssoReturnUrl is null unless the
      // return host is allowlisted (or loopback), so a crafted link can't exfiltrate the code.
      const ssoReturn = this.ssoReturnUrl;
      if (ssoReturn) {
        this.mcpAuthorizing = true; // reuse the "authorizing…" full-screen state
        try {
          const res = await window.axios.post(
            `${webAppConfig.baseURL}/api/account/sso/authorize`,
            { returnUri: ssoReturn.href },
            { headers: { Authorization: `Bearer ${LocalStorageService.getAccessToken()}` } }
          );
          if (res.data && res.data.redirectUrl) {
            window.location.href = res.data.redirectUrl;
            return;
          }
          this.mcpAuthorizing = false;
          this.$toasted.error('Admin SSO handoff failed — no redirect URL returned.');
        } catch (err) {
          console.error('[SSO]', err);
          this.mcpAuthorizing = false;
          this.$toasted.error('Admin SSO handoff failed.');
        }
        return;
      }
      if (this.$route.query.sso_return_uri && !ssoReturn) {
        // sso_return_uri present but not allowlisted — fail loudly rather than silently
        // falling through to dashboard, so misconfigured admin origins are obvious.
        this.$toasted.error('Admin SSO return URL is not on the trusted allowlist — login aborted.');
        // Strip the param from the real query string. The global router guard (main.js)
        // reads window.location.search, which persists across hash navigation — without
        // this, a never-allowlistable return URL would bounce the user back to /login on
        // every subsequent navigation, trapping them on the login page.
        try {
          const u = new URL(window.location.href);
          if (u.searchParams.has('sso_return_uri')) {
            u.searchParams.delete('sso_return_uri');
            window.history.replaceState(window.history.state, '', u.href);
          }
        } catch (e) { /* ignore */ }
        return;
      }

      if (
        this.$route.name == 'login' &&
        this.$route.params.action == 'mu' &&
        /iPhone|iPad|iPod|Android|Tablet/i.test(navigator.userAgent)
      ) {
        const lsWebData = LocalStorageService.getWebData(webAppConfig.baseServer);

        if (!lsWebData || lsWebData.prefDevice !== DeviceName.WEB) {
          const isRedirect = commons.redirectMobileView({
            appUrl: webAppConfig.previewAppURL,
            baseUrl: webAppConfig.baseURL,
            accessToken: LocalStorageService.getAccessToken(),
            refreshToken: LocalStorageService.getRefreshToken(),
            webData: lsWebData,
            workspaceId: $cookies.get('wsId')
          });

          if (isRedirect) {
            return true;
          }
        }
      }

      if ((this.$route.name == 'login' || this.$route.name == 'createAccount') && this.$route.query.solution) {
        this.$router.replace({ name: 'setup-workspace', query: { solution: this.$route.query.solution } });
      } else if (this.$route.name == 'createAccount' || this.$route.name == 'verify') {
        const workspaceId = await clientStore.dispatch('getFirstOrDefaultWorkspaceId');
        if (workspaceId) {
          this.$router.replace({ name: 'dashboard' });
        } else {
          this.$router.replace({ name: 'setup-workspace' });
        }
      } else if (this.$route.name == 'reset') {
        this.$router.push({ name: 'login' });
      } else if (this.$route.name == "join") {
        // A fresh joiner is a member of a populated workspace, not its designer — so
        // client-first sends them to the dashboard rather than the raw designer
        // (docs/frontend-client-home-unification-proposal.md D-1, ruled 2026-08-29).
        if (this.formData.existing || authorizationStore.getters.appClientFirstOnboarding) {
          location.href = "./#/dashboard";
        } else {
          location.href = "./#/studio";
        }
      } else {
        // A portal link opened while signed out (main.js kept it, for that route only): go on to
        // the portal rather than the dashboard. Consumed here, so it is followed once.
        const portalReturn = takePortalReturn();
        location.href = portalReturn ? "./#" + portalReturn : "./#/dashboard";
      }
    },
    async requestReset() {
      if (!this.loading) {
        this.$v.formData.$touch();
        if (!this.$v.formData.$invalid) {
          try {
            this.loading = true;
            this.onFocusLoadingOverlay();

            await authorizationStore.dispatch("forgotPassword", this.formData);

            this.$refs.ftsFeedbackIndicateModal.show({
              type: "success",
              title: "Check your mail",
              desc: "We have emailed you the instruction to reset your password.",
              handler: () => {
                this.$router.push({ name: 'login' });
              }
            });
          } catch (err) {
            this.$toasted.global.error(err);
          } finally {
            this.loading = false;
          }
        }
      }
    },
    async resetPassword() {
      if (!this.loading) {
        this.$v.formData.$touch();
        if (!this.$v.formData.$invalid) {
          try {
            this.loading = true;
            this.onFocusLoadingOverlay();

            await authorizationStore.dispatch("resetPassword", {
              email: this.formData.username,
              code: this.formData.code,
              password: this.formData.password,
            });
            this.$refs.ftsFeedbackIndicateModal.show({
              type: "success",
              title: "Your password has been reset",
              desc: "You now may continue with your new password.",
              handler: () => {
                this.$router.push({ name: 'login' });
              },
            });
          } catch (err) {
            this.$refs.ftsFeedbackIndicateModal.show({
              type: "error",
              uid: "resetError",
              response: err.response,
              handler: () => {
                this.$router.push({ name: 'login' });
              }
            });
          } finally {
            this.loading = false;
          }
        }
      }
    },
    async createAccount() {
      if (this.loading) return;
      // SS05894: MFA enrollment step — the account already exists; complete setup by binding the
      // authenticator secret via /token, then continue the original post-create flow.
      if (this.secret) {
        return this.completeCreateAccountMfa();
      }
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        try {
          this.loading = true;
          this.onFocusLoadingOverlay();

          if (this.isCreateAccountWithTemplate) {
            await authorizationStore.dispatch("register", {
              "email": this.formData.email,
              "templateId": this.$route.query.templateId
            });

            this.$refs.ftsFeedbackIndicateModal.show({
              type: "success",
              title: "Check your mail",
              desc: "You are almost there! We have sent an activation email. Please activate to verify your workspace.",
              handler: () => {
                this.$router.push({ name: 'login' });
              },
            });
          } else {
            const res = await authorizationStore.dispatch("createAccount", {
              "email": this.formData.email,
              "displayName": this.formData.displayName,
              "password": this.formData.password,
              "confirmPassword": this.formData.confirmPassword,
              "requiresMFA": this.formData.requiresMFA
            });

            // Email verification on: nothing was created yet — step 2 is the code screen.
            if (res && res.verification) {
              this.verification = res.verification;
              this.clientSecret = res.clientSecret;
              this.verificationError = null;
              this.verificationDone = false;
              return;
            }

            // MFA-enforced workspace: show the authenticator QR and wait for the OTP.
            if (res && res.error === '2fa_register') {
              const setup = JSON.parse(res.error_description);
              this.qrCode = setup['qrCodeUrl'];
              this.secret = setup['base32Secret'];
              this.otp = true;
              return;
            }

            await this.continueAfterCreate();
          }
        } catch (err) {
          this.$toasted.global.error(err);
        } finally {
          this.loading = false;
        }
      }
    },
    async completeCreateAccountMfa() {
      try {
        this.loading = true;
        this.onFocusLoadingOverlay();
        // Bind the authenticator secret and obtain a token via /token, then continue.
        await authorizationStore.dispatch("login", {
          "username": this.formData.email,
          "password": this.formData.password,
          "otp": this.formData.otp,
          "secret": this.secret,
          "requiresMFA": true
        });
        await this.continueAfterCreate();
      } catch (err) {
        if (err && err.response && err.response.data && err.response.data.error === 'invalid_grant') {
          this.$toasted.error('The code is incorrect. Please try again.');
        } else {
          this.$toasted.global.error(err);
        }
      } finally {
        this.loading = false;
      }
    },
    async continueAfterCreate() {
      if (this.isJoinExistingWorkspace) {
        await this.joinWorkspace();
      } else {
        if (this.isMcpFlow) {
          const payload = {
            companyName: `${this.formData.displayName} Workspace`,
            type: 'lite',
          };
          await authorizationStore.dispatch('setupWorkspace', payload);
        }
        this.redirect();
      }
    },
    /* email verification (docs/identity-email-verification-proposal.md) */
    async verifyCode(code) {
      if (this.verifying || !this.verification) return;
      try {
        this.verifying = true;
        this.verificationError = null;
        const res = await authorizationStore.dispatch("createAccountVerify", {
          id: this.verification.id,
          clientSecret: this.clientSecret,
          code,
        });
        this.verificationDone = true;

        // MFA-enforced join: the account now exists — show the authenticator QR and wait for the OTP.
        if (res && res.error === '2fa_register') {
          const setup = JSON.parse(res.error_description);
          this.qrCode = setup['qrCodeUrl'];
          this.secret = setup['base32Secret'];
          this.otp = true;
          return;
        }

        this.loading = true;
        this.onFocusLoadingOverlay();
        await this.continueAfterCreate();
      } catch (err) {
        const error = err && err.response && err.response.data && err.response.data.error;
        if (error === 'session_expired' || error === 'client_mismatch') {
          // Nothing was created and the form is still filled: one click starts a new session.
          this.$toasted.error(this.verificationMessage(err));
          this.changeEmail();
        } else {
          this.verificationError = this.verificationMessage(err);
        }
      } finally {
        this.verifying = false;
        this.loading = false;
      }
    },
    async resendCode() {
      if (this.verifying || !this.verification) return;
      try {
        this.verifying = true;
        this.verificationError = null;
        const res = await authorizationStore.dispatch("createAccountResend", {
          id: this.verification.id,
          clientSecret: this.clientSecret,
        });
        this.verification = res.verification;
        this.$toasted.success(`We sent a new code to ${this.verification.email}.`);
      } catch (err) {
        this.verificationError = this.verificationMessage(err);
      } finally {
        this.verifying = false;
      }
    },
    changeEmail() {
      // Back to the form with everything kept. The next Sign up starts a new session; the
      // abandoned one simply expires — nothing was created.
      this.verification = null;
      this.clientSecret = null;
      this.verificationError = null;
      this.verificationDone = false;
    },
    verificationMessage(err) {
      const data = err && err.response && err.response.data;
      if (data && (data.error_description || data.message)) return data.error_description || data.message;
      if (typeof err === 'string') return err;
      return 'Something went wrong. Please try again.';
    },
    async handleVerifyLanding() {
      const query = this.$route.query || {};
      try {
        this.loading = true;
        if (query.id && query.t) {
          // The "Verify and continue" button in the code email: proves the inbox and signs in this tab.
          const res = await authorizationStore.dispatch("createAccountLink", { id: query.id, token: query.t });
          if (res && res.error === '2fa_register') {
            this.verifyLanding = {
              title: 'Email verified',
              description: 'This workspace requires an authenticator app. Sign in to finish setting it up.',
              done: true,
              action: 'Sign in',
            };
            return;
          }
          this.verifyLanding = { title: 'Email verified', description: 'Taking you to your workspace…', done: false, action: null };
          await this.redirect();
        } else if (query.email && query.code) {
          // The legacy verification link (internal-domain login gate) finally has a page to land on.
          await authorizationStore.dispatch("verifyEmailLink", { email: query.email, code: query.code });
          this.verifyLanding = {
            title: 'Email verified',
            description: `${query.email} is confirmed. You can sign in now.`,
            done: true,
            action: 'Sign in',
          };
        } else {
          this.verifyLanding = {
            title: 'This link is not valid',
            description: 'Open the latest email we sent you, or start again from sign up.',
            done: true,
            action: 'Back to sign in',
          };
        }
      } catch (err) {
        const error = err && err.response && err.response.data && err.response.data.error;
        const used = error === 'session_completed';
        this.verifyLanding = {
          title: used ? 'This link was already used' : 'This link no longer works',
          description: used
            ? 'Your account is ready — sign in to continue.'
            : `${this.verificationMessage(err)} Start again from sign up to get a new code.`,
          done: true,
          action: used ? 'Sign in' : 'Back to sign in',
        };
      } finally {
        this.loading = false;
      }
    },
    async sendLoginVerificationEmail() {
      try {
        this.loading = true;
        await authorizationStore.dispatch("sendVerificationEmail", { email: this.formData.username });
        this.$toasted.success(`We sent a verification link to ${this.formData.username}.`);
      } catch (err) {
        this.$toasted.error(this.verificationMessage(err));
      } finally {
        this.loading = false;
      }
    },
    async submit() {
      if (!this.loading) {
        this.$v.formData.$touch();
        if (this.$v.formData.$invalid) {
          this.$toasted.error("Username and Password cannot be empty");
        } else {
          try {
            this.loading = true;
            this.onFocusLoadingOverlay();
            await authorizationStore.dispatch("login", {
              "username": this.formData.username,
              "password": this.formData.password,
              "otp": this.formData.otp,
              "secret": this.secret,
              // SS05897: relay the join workspace's 2FA policy so enrollment is prompted during sign-in.
              "requiresMFA": this.formData.requiresMFA
            });

            if (this.isJoinExistingWorkspace) {
              await this.joinWorkspace();
            } else {
              this.redirect();
            }
          } catch (err) {
            if (
              err.response &&
              err.response.data &&
              err.response.data.error_description
            ) {
              if (err.response.data.error == '2fa_register') {
                let payload = JSON.parse(err.response.data.error_description)
                this.qrCode = payload['qrCodeUrl'];
                this.secret = payload['base32Secret'];
                this.otp = true;
              } else if (err.response.data.error == '2fa_required') {
                this.otp = true;
              } else if (err.response.data.error == 'email_not_verified') {
                // The internal-domain gate (ApplicationOAuthProvider): offer the verification
                // link the server can already send, now that /verify exists to land it on.
                this.$refs.ftsFeedbackIndicateModal.show({
                  type: "error",
                  uid: "emailNotVerified",
                  title: "Verify your email to sign in",
                  desc: err.response.data.error_description,
                });
              } else {
                this.$toasted.error(err.response.data.error_description);
              }
            } else {
              this.$toasted.global.error(err);
            }
          } finally {
            this.loading = false;
          }
        }
      }
    },
    async joinWorkspace() {
      try {
        const res = await authorizationStore.dispatch("joinWorkspace", {
          registration: this.formData.registration,
          code: this.formData.code,
          secret: this.secret,
          otp: this.formData.otp
        });

        // SS05898: 2FA-enforced workspace, not yet enrolled — show the QR and wait for the OTP; this
        // same method re-runs with secret+otp (via the "Verify & join" action) to complete the join.
        if (res.data && res.data.error === '2fa_register') {
          const setup = JSON.parse(res.data.error_description);
          this.qrCode = setup['qrCodeUrl'];
          this.secret = setup['base32Secret'];
          this.otp = true;
          return;
        }

        if (res.data.workSpaceId) {
          $cookies.set('wsId', res.data.workSpaceId)
          LocalStorageService.setWorkspaceId(webAppConfig.baseServer, res.data.workSpaceId);
        }

        this.$router.push({ name: 'dashboard' });
      } catch (err) {
        throw err;
      }
    },
    async existingUserJoinWorkspace() {
      try {
        this.loading = true;
        await this.joinWorkspace();
      } catch (err) {
        this.$refs.ftsFeedbackIndicateModal.show({
          type: "error",
          title: "Something went wrong",
          response: err.response
        });
      } finally {
        this.loading = false;
      }
    },
    async activateWorkspace() {
      this.$v.formData.$touch();

      if ((this.state == "join" && !this.$v.formData.$invalid) || this.state === "activate") {
        try {
          this.loading = true;
          this.onFocusLoadingOverlay();

          const res = await authorizationStore.dispatch("activate", this.formData);

          if (res == true) {
            this.redirect();
          } else {
            this.$refs.ftsFeedbackIndicateModal.show({
              type: "success",
              title: "Check your mail",
              desc: "We have sent an activation email. Please activate through email",
              handler: () => {
                this.$router.push({ name: 'login' });
              },
            });
          }
        } catch (err) {
          this.$refs.ftsFeedbackIndicateModal.show({
            type: "error",
            uid: "resetError",
            title: "Something went wrong",
            response: err.response,
            handler: () => {
              this.$router.push({ name: 'login' });
            },
          });
        } finally {
          this.loading = false;
        }
      }
    },
    getWorkspaceShortName(name) {
      return commons.getWorkspaceShortName(name);
    },
    async updateUserProfile(payload) {
      await authorizationStore.dispatch("profile");
      const userProfile = authorizationStore.getters.userProfile;
      await authorizationStore.dispatch("updateUserProfile", {
        userName: userProfile.userName,
        displayName: payload.displayName,
        email: userProfile.email || userProfile.userName
      });
    },
    async thirdPartySignUp(type, authCode) {
      const payload = this.isCreateAccountWithTemplate ?
        {
          authCode: authCode,
          type: type,
          register: true,
          templateId: this.$route.query.templateId,
          ...this.formData
        } : {
          authCode: authCode,
          type: type,
          register: true,
          direct: true
        };
      const res = await authorizationStore.dispatch("externalSignUp", payload);
      if (res.redirect) {
        // externalSignUp returns { redirect }, not a raw axios response — reading res.data.redirect
        // here threw instead of redirecting, so a Google or Apple sign-up that answers with a
        // redirect (an account that has to finish elsewhere) died on this line.
        location.href = `${window.location.origin}/${res.redirect}`;
      } else {
        if (res.workspaces && res.workspaces.length) {
          res.setAuth();
        } else {
          const userProfile = await this.$refs.ftsLoginUserProfileModal.show();
          res.setAuth();
          await this.updateUserProfile(userProfile);
          if (this.isMcpFlow && !this.isJoinExistingWorkspace) {
            await authorizationStore.dispatch('setupWorkspace', {
              companyName: `${userProfile.displayName} Workspace`,
              type: 'lite',
            });
          }
        }
        if (this.isJoinExistingWorkspace) {
          await this.joinWorkspace();
        } else {
          this.redirect();
        }
      }
    },
    /* third-party sign-in SDKs: loaded side by side, never awaited by the form. Each button comes
       alive with its own SDK - until Google's has loaded its button reports the service as
       unavailable, and the Apple button mounts only once window.AppleID exists, because
       vue-apple-login initialises on mount. */
    loadSignInServices() {
      const loadScript = (src) => {
        return new Promise((resolve) => {
          if (document.querySelector(`script[src="${src}"]`)) {
            resolve(true);
            return;
          }

          const script = document.createElement('script');
          script.src = src;
          script.async = true;
          script.defer = true;

          script.onload = () => {
            resolve(true);
          };

          script.onerror = () => {
            console.warn(`Failed to load script: ${src}`);
            resolve(false);
          };

          document.head.appendChild(script);
        });
      };

      const google = loadScript("https://accounts.google.com/gsi/client").then((available) => {
        this.isGoogleServiceAvailable = available;
        if (available) {
          googleOAuth.initCodeClient({
            client_id: "664109851190-3ttfk39akgebtpo5rhmrdflt3nehuiie.apps.googleusercontent.com",
            scope: "https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile"
          });
        }
      });
      const apple = loadScript("https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js").then((available) => {
        this.isAppleServiceAvailable = available;
      });
      return Promise.all([google, apple]);
    },
    /* google register */
    async googleRegister() {
      try {
        const authCode = await googleOAuth.requestCode();
        this.loading = true;
        await this.thirdPartySignUp("google", authCode);
      } catch (err) {
        if (!this.isGoogleServiceAvailable) {
          this.$toasted.error("Google service is currently unavailable. Please try again later.");
        } else {
          this.$toasted.global.error(err);
        }
      } finally {
        this.loading = false;
      }
    },
    /* google login */
    async googleLogin() {
      try {
        const authCode = await googleOAuth.requestCode();
        this.loading = true;
        const response = await authorizationStore.dispatch("externalLogin", {
          authCode: authCode,
          type: "google"
        });

        if (this.isJoinExistingWorkspace) {
          await this.joinWorkspace();
        } else if (response === true) {
          this.redirect();
        } else if (response.data.redirect) {
          location.href = `${window.location.origin}/${response.data.redirect}`;
        }
      } catch (err) {
        if (!this.isGoogleServiceAvailable) {
          this.$toasted.error("Google service is currently unavailable. Please try again later.");
        } else {
          this.$toasted.global.error(err);
        }
      } finally {
        this.loading = false;
      }
    },
    /* apple register */
    async appleRegisterSuccess(data) {
      try {
        this.loading = true;
        await this.thirdPartySignUp("apple", data.authorization.code);
      } catch (err) {
        this.$toasted.global.error(err);
      } finally {
        this.loading = false;
      }
    },
    /* apple login */
    async onSuccess(data) {
      try {
        this.loading = true;
        this.formData.username = null;
        this.formData.password = null;
        const response = await authorizationStore.dispatch("externalLogin", {
          authCode: data.authorization.code,
          type: "apple"
        });

        if (this.isJoinExistingWorkspace) {
          await this.joinWorkspace();
        } else if (response === true) {
          this.redirect();
        } else if (response.data.redirect) {
          location.href = `${window.location.origin}/${response.data.redirect}`;
        }
      } catch (err) {
        this.$toasted.global.error(err);
      } finally {
        this.loading = false;
      }
    },
    onFailure(err) {
      if (err && err.detail && err.detail.error === 'popup_closed_by_user') {
        console.warn("Continue with Apple:", err.detail.error);
      } else if (!this.isAppleServiceAvailable) {
        this.$toasted.error("Apple service is currently unavailable. Please try again later.");
      } else {
        this.$toasted.global.error(err);
      }
    },
    logout() {
      authorizationStore.dispatch('logout');
    },
    async authorize() {
      // MCP consent: user is already signed in and clicked Authorize.
      // redirect() handles posting the JWT to the validated MCP callback.
      if (!(await this.ensureFreshMcpSignIn())) return;
      this.redirect();
    },
    /* SS06445 (ruled 2026-11-01): Authorize used to post whatever token was stored, so an expired one
       made the MCP connection mint answer 401. Renew a sign-in that has expired or is about to; when that
       fails, forget it and show the password form, keeping the MCP query (no logout(), which would
       replace the route with /login). */
    async ensureFreshMcpSignIn() {
      if (!LocalStorageService.getAccessToken()) return false;
      if (!accessTokenExpiring(LocalStorageService.getAuthorizationData())) return true;
      try {
        await authorizationStore.dispatch('refreshTokenQuietly');
        return true;
      } catch (err) {
        LocalStorageService.clearToken();
        this.isUserLoggedIn = false;
        return false;
      }
    },
    useDifferentAccount() {
      // Soft logout that preserves the MCP query params on the current route.
      // dispatch('logout') would router.replace to /login and drop mcp_nonce/
      // mcp_callback, so we clear the token directly instead.
      LocalStorageService.clearToken();
      this.isUserLoggedIn = false;
    },
    toggleStartFromScratch(val) {
      if (val) {
        this.formData.solutionId && (this.formData.solutionId = null);
        this.formData.aIMode && (this.formData.aIMode = false);
      }
    },
    // invitation code
    resetInvitationCodeForm() {
      this.invitationCodeStep = 1;
      this.invitationCodeFormData = {};
      this.$v.invitationCodeFormData.$reset();
    },
    async setInvitationCodeStep(step) {
      if (step === 3) {
        this.formData = this.invitationCodeFormData;
        this.joinExistingWorkspace = false;
        this.resetInvitationCodeForm();
      } else if (step === 2) {
        this.$v.invitationCodeFormData.$touch();
        if (this.$v.invitationCodeFormData.$invalid) return;

        // mock
        const mockAPICall = async (payload) => {
          await new Promise(resolve => setTimeout(resolve, 1500));

          if (payload.code === "playground") {
            return {
              data: {
                code: "playground-code",
                existing: true,
                registration: "playground-registration",
                workspace: "Playground"
              }
            }
          } else {
            throw { response: { data: { message: "Incorrect invitation code." } } }
          }
        }
        // end mock

        try {
          this.invitationCodeLoading = true;

          const res = await mockAPICall({
            code: this.invitationCodeFormData.invitationCode
          });

          this.invitationCodeFormData = res.data;
          this.invitationCodeStep = 2;
        } catch (err) {
          console.error(err);
          this.$toasted.global.error(err);
        } finally {
          this.invitationCodeLoading = false;
        }
      } else {
        this.resetInvitationCodeForm();
      }
    }
  },
  computed: {
    logoutError() {
      return authorizationStore.state.logoutError;
    },
    companyLogo() {
      return authorizationStore.getters.appLogo;
    },
    companyName() {
      return authorizationStore.getters.appName;
    },
    thirdPartyLogin() {
      return !authorizationStore.getters.appDisableThirdPartyLogin;
    },
    metaInfo() {
      if (this.$route && this.$route.meta && this.$route.meta.title) {
        return { title: this.$route.meta.title };
      } else if (this.$route.name === "join") {
        return {
          title: this.formData && this.formData.existing ? 'Join a workspace' : 'Setup a new workspace'
        }
      }
      return { title: 'Login' };
    },
    state() {
      return this.$route.name;
    },
    signupSteps() {
      return ['Details', 'Verify', 'Workspace'];
    },
    signupStep() {
      return this.verification && !this.otp ? 2 : 1;
    },
    pageState() {
      if (
        this.$route.params.action &&
        this.$route.params.action.toLowerCase() === 'join' &&
        this.$route.query.id
      ) {
        return "join";
      }

      return this.state;
    },
    getUserDisplayName() {
      if (authorizationStore.getters.userProfile) {
        return authorizationStore.getters.userProfile.displayName || authorizationStore.getters.userProfile.userName;
      }
    },
    isCreateAccountWithTemplate() {
      return Boolean(this.$route.query && this.$route.query.templateId);
    },
    hasMcpParams() {
      // True whenever both MCP query params are present, regardless of trust.
      // Use isMcpFlow for "this is a *valid* MCP flow we can act on".
      return Boolean(
        this.$route.query &&
        this.$route.query.mcp_nonce &&
        this.$route.query.mcp_callback
      );
    },
    mcpCallbackUrl() {
      // Returns a parsed URL only when the callback is well-formed AND the host
      // is on the allowlist. Used by isMcpFlow, mcpClientHost and redirect() so
      // the banner never shows attacker-controlled hostnames and the JWT is
      // never POSTed to an untrusted origin. Allowlist is sourced from
      // webAppConfig.allowedMcpHosts (so staging/dev can extend it) and falls
      // back to the production host.
      const cb = this.$route.query && this.$route.query.mcp_callback;
      if (!cb) return null;
      let url;
      try { url = new URL(cb); } catch (e) { return null; }
      const allowedHosts = (Array.isArray(webAppConfig.allowedMcpHosts) && webAppConfig.allowedMcpHosts.length)
        ? webAppConfig.allowedMcpHosts
        : ['mcp.inistate.com'];
      const isLoopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
      const hostAllowed = allowedHosts.includes(url.host) || isLoopback;
      const protocolOk = url.protocol === 'https:' || (isLoopback && url.protocol === 'http:');
      return (hostAllowed && protocolOk) ? url : null;
    },
    isMcpFlow() {
      return Boolean(
        this.$route.query &&
        this.$route.query.mcp_nonce &&
        this.mcpCallbackUrl
      );
    },
    ssoReturnUrl() {
      // Parsed return URL for the admin-panel SSO handoff, but only when the host is on
      // the allowlist (or loopback) and the protocol is safe. Null otherwise, so redirect()
      // never hands a one-time code to an untrusted origin. Mirrors mcpCallbackUrl.
      const ret = this.$route.query && this.$route.query.sso_return_uri;
      if (!ret) return null;
      let url;
      try { url = new URL(ret); } catch (e) { return null; }
      const allowedHosts = (Array.isArray(webAppConfig.allowedAdminHosts) && webAppConfig.allowedAdminHosts.length)
        ? webAppConfig.allowedAdminHosts
        : ['admin.inistate.com'];
      const isLoopback = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
      const hostAllowed = allowedHosts.includes(url.host) || isLoopback;
      const protocolOk = url.protocol === 'https:' || (isLoopback && url.protocol === 'http:');
      return (hostAllowed && protocolOk) ? url : null;
    },
    mcpClientHost() {
      return this.mcpCallbackUrl ? this.mcpCallbackUrl.host : '';
    },
    mcpClientName() {
      // Display name passed by the MCP server (sourced from RFC 7591 DCR
      // client_name). Untrusted: cap length and let Vue's mustache escape
      // handle HTML safety. Falls back to the callback host so the banner
      // is never empty.
      const raw = this.$route.query && this.$route.query.mcp_client_name;
      if (typeof raw === 'string' && raw.trim()) {
        return raw.trim().slice(0, 64);
      }
      return this.mcpClientHost;
    },
    isJoinExistingWorkspace() {
      return (
        this.formData && this.formData.workspace &&
        (
          this.$route.query && this.$route.query.id ||
          this.formData.registration && this.formData.code
        )
      );
    }
  },
  watch: {
    state(newVal, oldVal) {
      if (newVal !== oldVal) {
        // reset
        this.$v.$reset();
        this.formData = {};
        this.isError = false;
        this.qrCode = null;
        this.secret = null;
        this.otp = false;
        this.verification = null;
        this.clientSecret = null;
        this.verificationError = null;
        this.verificationDone = false;
        this.$refs.ftsFeedbackIndicate.reset();
        this.$refs.ftsFeedbackIndicateModal.reset();
        this.init();
      }
    },
  },
  async mounted() {
    // config
    await webAppConfig.getConfig();

    // The form never waits for the third-party SDKs: a script request that stalls without failing
    // settles neither onload nor onerror, and awaiting it here left the sign-in page blank
    // (Automated Web Tests #660: appleid.auth.js never answered).
    this.loadSignInServices();

    await this.init();
    this.loaded = true;
  },
  beforeDestroy() {
    commons.hideCrispChat();
  }
};
</script>

<style lang="scss">
@use "./src/assets/scss/components/login";
</style>
